import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { busStopsList, studentBusEnroll } from "../../api/Transport";

import { studentsListOnly } from "../../api/Student";

import { SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";

export default function StudentBusEnroll() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParam = new URLSearchParams(location.search);
  const student_id = queryParam.get("student_id");
  const stop_id = queryParam.get("stop_id");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [stops, setStops] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentDropdownOpen, setStudentDropdownOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const [stopDropdownOpen, setStopDropdownOpen] = useState(false);
  const [stopSearch, setStopSearch] = useState("");

  const filteredStudents = students?.filter((student) => {
    const search = studentSearch.toLowerCase().trim();
    if (!search) return true;
    return (
      String(student.name || "")
        .toLowerCase()
        .includes(search) ||
      String(student.registration_no || "")
        .toLowerCase()
        .includes(search) ||
      String(student.phone || "")
        .toLowerCase()
        .includes(search)
    );
  });

  const sortedStops = [...(stops || [])].sort((a, b) =>
    (a?.stop_name || "").localeCompare(b?.stop_name || "", undefined, {
      sensitivity: "base",
    }),
  );

  const filteredStops = sortedStops?.filter((item) => {
    const search = stopSearch.toLowerCase().trim();
    if (!search) return true;
    return (
      String(item.stop_name || "")
        .toLowerCase()
        .includes(search) ||
      String(item?.bus?.bus_number || "")
        .toLowerCase()
        .includes(search) ||
      String(item?.bus?.driver_name || "")
        .toLowerCase()
        .includes(search)
    );
  });

  const [data, setData] = useState({
    session_id: "",
    student_id: "",
    bus_stop_id: "",
    selectedStop: null,
    payment_type: "Monthly",
    joining_date: new Date().toISOString().split("T")[0],
    final_fee: 0,
    discount_remark: "",
  });

  const [discountData, setDiscountData] = useState({
    type: "fixed",
    value: "",
  });

  const getStopsList = async () => {
    setLoading(true);

    try {
      let addUrl = "limit=all";

      if (data?.session_id) {
        addUrl += `&session_id=${data.session_id}`;
      }

      const response = await busStopsList(addUrl);

      if (response?.data?.status) {
        setStops(response?.data?.data || []);
      } else {
        setStops([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load bus stops.",
        );
      }
    } catch (error) {
      setStops([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getStudentList = async () => {
    setLoading(true);

    try {
      let addUrl = "";

      if (data?.session_id) {
        addUrl += `session_id=${data.session_id}`;
      }

      const response = await studentsListOnly(addUrl);

      if (response?.data?.status) {
        setStudents(response?.data?.data || []);
      } else {
        setStudents([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load students.",
        );
      }
    } catch (error) {
      setStudents([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    try {
      const parseSessions = JSON.parse(
        localStorage.getItem("sessions") ?? "[]",
      );

      setSessions(Array.isArray(parseSessions) ? parseSessions : []);
    } catch (error) {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    const activeSession = sessions?.find((s) => Number(s.status) === 1);

    if (!activeSession) return;

    setData((prev) => ({
      ...prev,
      session_id: prev.session_id || activeSession.id,
    }));
  }, [sessions]);

  useEffect(() => {
    if (!data?.session_id) return;

    getStopsList();
  }, [data?.session_id]);

  useEffect(() => {
    if (!data?.session_id) return;

    getStudentList();
  }, [data?.session_id]);

  useEffect(() => {
    if (!student_id) return;

    setData((prev) => ({
      ...prev,
      student_id,
    }));
  }, [student_id]);

  useEffect(() => {
    if (!stop_id) return;

    setData((prev) => ({
      ...prev,
      bus_stop_id: stop_id,
    }));
  }, [stop_id]);

  useEffect(() => {
    if (!data?.bus_stop_id) {
      setData((prev) => ({
        ...prev,
        selectedStop: null,
        final_fee: 0,
      }));

      return;
    }

    const chosen = stops?.find(
      (item) => Number(item.id) === Number(data.bus_stop_id),
    );

    if (!chosen) return;

    const stopFee = Number(chosen.stop_fee || 0);

    setData((prev) => ({
      ...prev,
      selectedStop: {
        ...chosen,
        final_fee: stopFee,
      },
      final_fee: stopFee,
      discount_remark: "",
    }));

    setDiscountData({
      type: "fixed",
      value: "",
    });
  }, [data?.bus_stop_id, stops]);

  const calculateDiscount = (type, value) => {
    if (!data?.selectedStop) return;

    const totalFee = Number(data.selectedStop.stop_fee || 0);

    let discountValue = Number(value || 0);

    if (discountValue < 0) {
      discountValue = 0;
    }

    if (type === "fixed") {
      discountValue = Math.min(discountValue, totalFee);
    } else {
      discountValue = Math.min(discountValue, 100);
    }

    let expectedFinal = 0;

    if (type === "fixed") {
      expectedFinal = totalFee - discountValue;
    } else {
      expectedFinal = totalFee - (totalFee * discountValue) / 100;
    }

    expectedFinal = Math.max(0, Math.round(expectedFinal));

    setData((prev) => ({
      ...prev,

      final_fee: expectedFinal,

      selectedStop: prev.selectedStop
        ? {
            ...prev.selectedStop,
            final_fee: expectedFinal,
          }
        : null,
    }));
  };

  const handleSessionChange = (e) => {
    const sessionId = e.target.value;

    setData((prev) => ({
      ...prev,
      session_id: sessionId,
      bus_stop_id: "",
      selectedStop: null,
      final_fee: 0,
      discount_remark: "",
    }));

    setDiscountData({
      type: "fixed",
      value: "",
    });
  };

  const handlePaymentTypeChange = (e) => {
    setData((prev) => ({
      ...prev,
      payment_type: e.target.value,
    }));
  };

  const handleJoiningDateChange = (e) => {
    setData((prev) => ({
      ...prev,
      joining_date: e.target.value,
    }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!data?.session_id) {
      SweetAlertShow("info", "Warning", "Please select a session.");
      return;
    }

    if (!data?.student_id) {
      SweetAlertShow("info", "Warning", "Please select a student.");
      return;
    }

    if (!data?.bus_stop_id || !data?.selectedStop?.id) {
      SweetAlertShow("info", "Warning", "Please select a stop & bus.");
      return;
    }

    if (!data?.payment_type) {
      SweetAlertShow("info", "Warning", "Please select payment type.");
      return;
    }

    if (!data?.joining_date) {
      SweetAlertShow("info", "Warning", "Please select joining date.");
      return;
    }

    if (Number(data.final_fee) > Number(data.selectedStop.stop_fee)) {
      SweetAlertShow(
        "info",
        "Warning",
        "Final fee cannot be greater than stop fee.",
      );
      return;
    }

    const discountAmount =
      Number(data.selectedStop.stop_fee || 0) - Number(data.final_fee || 0);

    if (discountAmount > 0 && !data.discount_remark?.trim()) {
      SweetAlertShow("info", "Warning", "Please enter discount remark.");
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("session_id", data.session_id);
      formData.append("student_id", data.student_id);
      formData.append("bus_id", data.selectedStop.bus_id);
      formData.append("bus_stop_id", data.selectedStop.id);
      formData.append("final_fee", Number(data.final_fee || 0));
      formData.append("payment_type", data.payment_type);
      formData.append("joining_date", data.joining_date);
      if (data.discount_remark?.trim()) {
        formData.append("discount_remark", data.discount_remark.trim());
      }
      const response = await studentBusEnroll(formData);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message ||
            "Student enrolled in bus service successfully!",
        );

        navigate(`/student/details?id=${data.student_id}`);

        return;
      }

      SweetAlertShow(
        "info",
        "Warning",
        Array.isArray(response?.data?.message)
          ? response.data.message.join(", ")
          : response?.data?.message || "Unable to enroll student.",
      );
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message ??
        "Something went wrong!";

      SweetAlertShow(
        "error",
        "Error",
        Array.isArray(message) ? message.join(", ") : message,
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedStop = data?.selectedStop;
  const discountAmount = selectedStop
    ? Math.max(
        0,
        Number(selectedStop.stop_fee || 0) -
          Number(selectedStop.final_fee || 0),
      )
    : 0;

  const selectedStudent = students?.find(
    (student) => String(student.id) === String(data?.student_id),
  );

  const FieldSkeleton = () => (
    <div className="placeholder-glow">
      <span
        className="placeholder col-12"
        style={{
          height: 56,
          borderRadius: 8,
        }}
      />
    </div>
  );

  return (
    <>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <div className="col-12">
                    <div className="card border-0 shadow-sm mb-3">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              style={{
                                width: 48,
                                height: 48,
                                borderRadius: "14px",
                                background:
                                  "linear-gradient(135deg,#185fa5,#4f8cff)",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 22,
                                boxShadow: "0 4px 12px rgba(24,95,165,0.25)",
                              }}
                            >
                              <i className="ri-bus-2-line" />
                            </div>

                            <div>
                              <h5 className="m-0 fw-bold">
                                Student Bus Enrollment
                              </h5>

                              <small className="text-muted">
                                Assign a student to a route stop
                              </small>
                            </div>
                          </div>

                          {/* Student */}

                          <div style={{ minWidth: 260 }}>
                            <div
                              className="position-relative"
                              style={{
                                zIndex: studentDropdownOpen ? 20 : 1,
                              }}
                            >
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search Student"
                                  value={
                                    studentDropdownOpen
                                      ? studentSearch
                                      : selectedStudent
                                        ? `${selectedStudent.name} (${selectedStudent.registration_no})`
                                        : ""
                                  }
                                  onFocus={() => {
                                    setStudentDropdownOpen(true);
                                    setStudentSearch("");
                                  }}
                                  onChange={(e) => {
                                    setStudentSearch(e.target.value);
                                    setStudentDropdownOpen(true);
                                  }}
                                  autoComplete="off"
                                />

                                <label>
                                  <i className="ri-user-3-line me-1" />
                                  Select Student
                                </label>
                              </div>

                              {studentDropdownOpen && (
                                <div
                                  className="position-absolute bg-white border rounded-3 shadow w-100 mt-1"
                                  style={{
                                    maxHeight: 300,
                                    overflowY: "auto",
                                    zIndex: 1000,
                                  }}
                                >
                                  {filteredStudents.length > 0 ? (
                                    filteredStudents.map((student) => (
                                      <div
                                        key={student.id}
                                        className="px-3 py-2 border-bottom"
                                        style={{
                                          cursor: "pointer",
                                          background:
                                            String(data.student_id) ===
                                            String(student.id)
                                              ? "#f0f4ff"
                                              : "#fff",
                                        }}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                          setData((prev) => ({
                                            ...prev,
                                            student_id: String(student.id),
                                          }));

                                          setStudentSearch("");
                                          setStudentDropdownOpen(false);
                                        }}
                                      >
                                        <div className="fw-semibold">
                                          {student.name}
                                        </div>

                                        <small className="text-muted">
                                          {student.registration_no} ·{" "}
                                          {student.phone}
                                        </small>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="text-center text-muted py-4">
                                      <i className="ri-user-search-line fs-3 d-block mb-1" />
                                      No student found
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Selected Student */}

                        {selectedStudent && (
                          <div
                            className="d-flex align-items-center gap-2 mt-3 p-2 rounded-3"
                            style={{
                              background: "#f8f9fb",
                            }}
                          >
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center bg-label-primary"
                              style={{
                                width: 32,
                                height: 32,
                                flexShrink: 0,
                              }}
                            >
                              <i className="ri-user-3-fill" />
                            </div>

                            <small>
                              <strong>{selectedStudent.name}</strong> ·{" "}
                              {selectedStudent.registration_no} ·{" "}
                              {selectedStudent.phone}
                            </small>
                          </div>
                        )}
                      </div>
                    </div>

                    <form onSubmit={handleAdd}>
                      <div className="card border-0 shadow-sm mb-3">
                        <div className="card-body p-4">
                          <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                            <i className="ri-route-line text-primary" />
                            Route Selection
                          </h6>

                          <div className="row g-3">
                            {/* Session */}

                            <div className="col-md-6">
                              {loading && sessions.length === 0 ? (
                                <FieldSkeleton />
                              ) : (
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    name="session_id"
                                    value={data?.session_id || ""}
                                    onChange={handleSessionChange}
                                  >
                                    <option value="">Select Session</option>

                                    {sessions?.map((session) => (
                                      <option
                                        key={session.id}
                                        value={session.id}
                                      >
                                        {session.session} :{" "}
                                        {Number(session.status) === 1
                                          ? "Active"
                                          : "Inactive"}
                                      </option>
                                    ))}
                                  </select>

                                  <label>
                                    <i className="ri-calendar-line me-1" />
                                    Select Session
                                  </label>
                                </div>
                              )}
                            </div>

                            {/* Stop */}

                            <div className="col-md-6">
                              {loading ? (
                                <FieldSkeleton />
                              ) : (
                                <>
                                  <div className="form-floating form-floating-outline">
                                    <div
                                      className="position-relative"
                                      style={{
                                        zIndex: stopDropdownOpen ? 20 : 1,
                                      }}
                                    >
                                      <div className="form-floating form-floating-outline">
                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="Search Stop & Bus"
                                          value={
                                            stopDropdownOpen
                                              ? stopSearch
                                              : selectedStop
                                                ? `${selectedStop.stop_name} (${selectedStop?.bus?.bus_number || "-"})`
                                                : ""
                                          }
                                          onFocus={() => {
                                            setStopDropdownOpen(true);
                                            setStopSearch("");
                                          }}
                                          onChange={(e) => {
                                            setStopSearch(e.target.value);
                                            setStopDropdownOpen(true);
                                          }}
                                          autoComplete="off"
                                        />

                                        <label>
                                          <i className="ri-map-pin-2-line me-1" />
                                          Select Stop & Bus
                                        </label>
                                      </div>

                                      {stopDropdownOpen && (
                                        <div
                                          className="position-absolute bg-white border rounded-3 shadow w-100 mt-1"
                                          style={{
                                            maxHeight: 300,
                                            overflowY: "auto",
                                            zIndex: 1000,
                                          }}
                                        >
                                          {filteredStops.length > 0 ? (
                                            filteredStops.map((item) => (
                                              <div
                                                key={item.id}
                                                className="px-3 py-2 border-bottom"
                                                style={{
                                                  cursor: "pointer",
                                                  background:
                                                    String(data.bus_stop_id) ===
                                                    String(item.id)
                                                      ? "#f0f4ff"
                                                      : "#fff",
                                                }}
                                                onMouseDown={(e) =>
                                                  e.preventDefault()
                                                }
                                                onClick={() => {
                                                  setData((prev) => ({
                                                    ...prev,
                                                    bus_stop_id: String(
                                                      item.id,
                                                    ),
                                                  }));

                                                  setStopSearch("");
                                                  setStopDropdownOpen(false);
                                                }}
                                              >
                                                <div className="fw-semibold">
                                                  <i className="ri-map-pin-2-line me-1 text-primary" />
                                                  {item.stop_name}
                                                </div>

                                                <small className="text-muted">
                                                  <i className="ri-bus-2-line me-1" />
                                                  {item?.bus?.bus_number || "-"}
                                                  {" · "}
                                                  {item?.bus?.driver_name ||
                                                    "Driver not assigned"}
                                                </small>

                                                <small className="text-success d-block mt-1">
                                                  ₹{item?.stop_fee || 0}
                                                </small>
                                              </div>
                                            ))
                                          ) : (
                                            <div className="text-center text-muted py-4">
                                              <i className="ri-search-line fs-3 d-block mb-1" />
                                              No stop or bus found
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {selectedStop?.id ? (
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-body p-4">
                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                              <i className="ri-calendar-check-line text-primary" />
                              Enrollment Details
                            </h6>

                            <div className="row g-3">
                              <div className="col-md-4">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="date"
                                    className="form-control"
                                    name="joining_date"
                                    value={data?.joining_date || ""}
                                    onChange={handleJoiningDateChange}
                                    required
                                  />

                                  <label>
                                    <i className="ri-calendar-event-line me-1" />
                                    Joining Date
                                  </label>
                                </div>
                              </div>

                              {/* Payment Type */}

                              <div className="col-md-4">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    name="payment_type"
                                    value={data?.payment_type || "Monthly"}
                                    onChange={handlePaymentTypeChange}
                                    required
                                  >
                                    <option value="Monthly">Monthly</option>

                                    <option value="Quarterly">Quarterly</option>

                                    <option value="Half Year">Half Year</option>

                                    <option value="Full Year">Full Year</option>
                                  </select>

                                  <label>
                                    <i className="ri-calendar-schedule-line me-1" />
                                    Payment Type
                                  </label>
                                </div>
                              </div>

                              {/* Bus */}

                              <div className="col-md-4">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={selectedStop?.bus?.bus_number || ""}
                                    readOnly
                                  />

                                  <label>
                                    <i className="ri-bus-2-line me-1" />
                                    Bus
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {selectedStop?.id ? (
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-body p-4">
                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                              <i className="ri-money-rupee-circle-line text-success" />
                              Fee & Discount
                            </h6>

                            <div className="row g-3 align-items-end">
                              {/* Stop Fee */}

                              <div className="col-6 col-md-3">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={selectedStop.stop_fee || 0}
                                    readOnly
                                  />

                                  <label>
                                    <i className="ri-price-tag-3-line me-1" />
                                    Stop Fee
                                  </label>
                                </div>
                              </div>

                              {/* Discount Type */}

                              <div className="col-6 col-md-3">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    value={discountData.type}
                                    onChange={(e) => {
                                      const type = e.target.value;

                                      setDiscountData((prev) => ({
                                        ...prev,
                                        type,
                                      }));

                                      calculateDiscount(
                                        type,
                                        discountData.value,
                                      );
                                    }}
                                  >
                                    <option value="fixed">Fixed ₹</option>

                                    <option value="percentage">
                                      Percentage %
                                    </option>
                                  </select>

                                  <label>
                                    <i className="ri-percent-line me-1" />
                                    Discount Type
                                  </label>
                                </div>
                              </div>

                              {/* Discount Value */}

                              <div className="col-6 col-md-3">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="number"
                                    className="form-control"
                                    min="0"
                                    step="0.01"
                                    placeholder="Discount"
                                    value={discountData.value}
                                    onChange={(e) => {
                                      const value = Number(e.target.value || 0);

                                      setDiscountData((prev) => ({
                                        ...prev,
                                        value,
                                      }));

                                      calculateDiscount(
                                        discountData.type,
                                        value,
                                      );
                                    }}
                                  />

                                  <label>
                                    <i className="ri-coupon-3-line me-1" />

                                    {discountData.type === "fixed"
                                      ? "Discount ₹"
                                      : "Discount %"}
                                  </label>
                                </div>
                              </div>

                              {/* Final Fee */}

                              <div className="col-6 col-md-3">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="number"
                                    name="final_fee"
                                    className="form-control fw-bold"
                                    value={data?.final_fee || 0}
                                    readOnly
                                  />

                                  <label>
                                    <i className="ri-checkbox-circle-line me-1" />
                                    Final Fee
                                  </label>
                                </div>
                              </div>

                              {/* Discount Remark */}

                              {discountAmount > 0 && (
                                <div className="col-12">
                                  <div className="form-floating form-floating-outline">
                                    <input
                                      type="text"
                                      className="form-control"
                                      name="discount_remark"
                                      placeholder="Discount Remark"
                                      value={data?.discount_remark || ""}
                                      onChange={(e) =>
                                        setData((prev) => ({
                                          ...prev,
                                          discount_remark: e.target.value,
                                        }))
                                      }
                                      required
                                    />

                                    <label>
                                      <i className="ri-sticky-note-line me-1" />
                                      Discount Remark
                                    </label>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Discount Summary */}

                            {discountAmount > 0 && (
                              <div className="d-flex align-items-center gap-2 mt-3 p-2 rounded-3 bg-label-success">
                                <i className="ri-discount-percent-line" />

                                <small>
                                  ₹{discountAmount} discount applied on ₹
                                  {selectedStop.stop_fee} — final fee ₹
                                  {data.final_fee}
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        !loading && (
                          <div className="card border-0 shadow-sm mb-3">
                            <div className="card-body text-center py-5">
                              <i
                                className="ri-map-pin-line d-block mx-auto mb-2"
                                style={{
                                  fontSize: 44,
                                  opacity: 0.25,
                                }}
                              />

                              <div className="fw-semibold">
                                No stop selected
                              </div>

                              <small className="text-muted">
                                Choose a session and stop above to see fee
                                details
                              </small>
                            </div>
                          </div>
                        )
                      )}

                      {selectedStop?.id && (
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-body p-4">
                            <div className="row g-3">
                              <div className="col-md-3 col-6">
                                <small className="text-muted d-block">
                                  Student
                                </small>

                                <strong>{selectedStudent?.name || "-"}</strong>
                              </div>

                              <div className="col-md-3 col-6">
                                <small className="text-muted d-block">
                                  Bus
                                </small>

                                <strong>
                                  {selectedStop?.bus?.bus_number || "-"}
                                </strong>
                              </div>

                              <div className="col-md-3 col-6">
                                <small className="text-muted d-block">
                                  Payment Type
                                </small>

                                <strong>{data.payment_type}</strong>
                              </div>

                              <div className="col-md-3 col-6">
                                <small className="text-muted d-block">
                                  Final Fee
                                </small>

                                <strong className="text-success">
                                  ₹{data.final_fee || 0}
                                </strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="d-flex justify-content-end">
                        <button
                          type="submit"
                          disabled={
                            saving ||
                            !selectedStop?.id ||
                            !data?.student_id ||
                            !data?.session_id
                          }
                          className="btn btn-primary px-4 d-flex align-items-center gap-2"
                        >
                          {saving ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              />
                              Saving…
                            </>
                          ) : (
                            <>
                              <i className="ri-save-line" />
                              Save Enrollment
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
